// 지도 위치·동 이름이 불확실한 학생의 보호자에게 주소를 문자로 요청하고, 회신받은 주소를 입력해 위치 찾아 저장하는 페이지.
import { useMemo, useState } from "react";
import { useStudents } from "@/hooks/useStudents";
import { Student } from "@/types";
import { doc, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/lib/firebase";
import { geocodeAddress } from "@/lib/vworld";
import { deriveBuildingName, normalizeBungil } from "@/lib/excelParse";

import { normalizeGu } from "@/lib/region";
import { plainError } from "@/lib/plainError";
const requestSms = httpsCallable<{ studentId: string }, { ok: boolean; reason?: string }>(
  functions,
  "requestAddressSms",
);

// 주소 확인이 필요한 학생: 지도 위치/동 이름 누락 또는 추정(needs_review).
function needsConfirm(s: Student): boolean {
  return !s.lat || !s.lng || !s.dong || s.geocodeStatus === "needs_review";
}

export default function AddressConfirm() {
  const { students, loading } = useStudents();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<Record<string, string>>({});

  const targets = useMemo(() => students.filter(needsConfirm), [students]);

  const sendSms = async (s: Student) => {
    setBusy((b) => ({ ...b, [s.id]: "문자 발송 중…" }));
    try {
      const res = await requestSms({ studentId: s.id });
      const d = res.data;
      setBusy((b) => ({ ...b, [s.id]: d.ok ? "✅ 문자 발송됨" : `⚠️ 발송 실패: ${d.reason}` }));
    } catch (e: any) {
      setBusy((b) => ({ ...b, [s.id]: `⚠️ ${plainError(e, "발송 실패")}` }));
    }
  };

  const saveAddress = async (s: Student) => {
    // 번길 띄어쓰기 정규화 — 어긋난 표기는 지오코더가 다른 길로 스냅한다(함정 #8)
    const addr = normalizeBungil((drafts[s.id] || "").replace(/\s+/g, " ").trim());
    if (!addr) return;
    setBusy((b) => ({ ...b, [s.id]: "위치 찾는 중…" }));
    try {
      const p = await geocodeAddress(addr);
      if (!p) {
        setBusy((b) => ({ ...b, [s.id]: "❌ 지도에서 위치를 못 찾았습니다 — 주소를 다시 확인하세요" }));
        return;
      }
      if (p.roadMismatch) {
        const goOn = window.confirm(
          `⚠️ 지도 검색이 입력 주소와 다른 길로 매칭했습니다.\n\n입력: ${addr}\n매칭: ${p.matched}\n\n주소 표기(번길 숫자·띄어쓰기)를 다시 확인하세요. 그래도 이대로 저장할까요?`,
        );
        if (!goOn) {
          setBusy((b) => ({ ...b, [s.id]: "⏸ 저장 취소 — 주소를 확인한 뒤 다시 시도하세요" }));
          return;
        }
      }
      const bn = deriveBuildingName(addr);
      await updateDoc(doc(db, "students", s.id), {
        refinedAddress: addr,
        ...(bn ? { buildingName: bn } : {}),
        lat: p.y,
        lng: p.x,
        gu: normalizeGu(p.gu) || s.gu, // "성남시 분당구" → "분당구"
        dong: p.dong || s.dong,
        geocodeStatus: "ok",
        geocodeNeedsCheck: false,
        geocodedAt: new Date(),
      });
      setBusy((b) => ({ ...b, [s.id]: `✅ 저장됨 (${p.gu || ""} ${p.dong || ""})` }));
    } catch (e: any) {
      setBusy((b) => ({ ...b, [s.id]: `❌ ${plainError(e, "저장 실패")}` }));
    }
  };

  const sendAll = async () => {
    for (const s of targets) await sendSms(s);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-bold">📮 주소 확인 대기 ({targets.length}명)</h1>
        {targets.length > 0 && (
          <button className="btn-secondary text-sm" onClick={sendAll}>
            전체 문자 발송 ({targets.length}명)
          </button>
        )}
      </div>
      <p className="text-sm text-slate-500">
        지도 위치·동 이름이 불확실한 학생입니다. 보호자에게 <b>주민등록증상 주소</b>를 문자로 요청하고, 회신받은 주소를
        입력하면 지도 위치를 찾아 저장합니다.
      </p>

      {loading && <div className="card p-4 text-center text-slate-400">불러오는 중…</div>}
      {!loading && targets.length === 0 && (
        <div className="card p-6 text-center text-emerald-600 font-semibold">🎉 주소 확인이 필요한 학생이 없습니다!</div>
      )}

      <div className="space-y-2">
        {targets.map((s) => (
          <div key={s.id} className="card p-3 space-y-2">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div>
                <div className="font-bold">{s.school} {s.name}</div>
                <div className="text-xs text-slate-500">보호자 {s.phone || "번호 없음"}</div>
                <div className="text-xs text-slate-500 mt-0.5">현재: {s.refinedAddress || s.rawAddress || "—"}</div>
                <div className="text-2xs text-slate-400">
                  지도 위치 {s.lat ? "있음" : "없음"} · 동 이름 {s.dong || "없음"}
                  {s.geocodeStatus === "needs_review" && " · ⚠️ 추정값"}
                </div>
              </div>
              <button className="btn-ghost text-xs shrink-0" onClick={() => sendSms(s)}>
                📩 문자 발송
              </button>
            </div>
            <div className="flex gap-2">
              <input
                className="input text-sm flex-1"
                placeholder="회신받은 주민등록증상 주소 입력 (도로명·동·호)"
                value={drafts[s.id] || ""}
                onChange={(e) => setDrafts((d) => ({ ...d, [s.id]: e.target.value }))}
              />
              <button className="btn-primary btn-sm shrink-0" onClick={() => saveAddress(s)}>
                위치 찾아 저장
              </button>
            </div>
            {busy[s.id] && <div className="text-xs text-slate-600">{busy[s.id]}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
